<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import LogoReferences from "@components/LogoReferences.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { PartnerPlacement } from "@core/schema/PartnerPlacement.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getIndustriesPath } from "@core/schema/paths/getIndustriesPath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsIntegrations } from "../data/getFaqsIntegrations.js";
	import { getHowWeDoIntegrations } from "../data/getHowWeDoIntegrations.js";
	import { getPartnerLogos } from "../data/getPartnerLogos.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getReasonsIntegrations } from "../data/getReasonsIntegrations.js";
	import { getSystems } from "../data/getSystems.js";
	import type { IntegrationsProps } from "./IntegrationsProps.js";

	const { tenant } = getGlobalTemplateProps();
	const { partners }: IntegrationsProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="cg[[Integrací]] systémů zbavíme vaše podnikání cg[[chaosu]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Propojením firemních systémů vám pomůžeme sp[[skoncovat]] s manuálním přenášením dat, sp[[minimalizovat chyby]] v záznamech nebo sp[[zbavit se starostí]] s používáním desítek nástrojů."
				/>
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Zeptat se na podrobnosti" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Zajistíme cg[[hladkou spolupráci]] mezi vašimi systémy a aplikacemi" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Žádné podnikání nemůže růst, pokud se utápí v chaosu a neefektivních manuálních procesech, které zaberou i desítky hodin času. sp[[Jak z toho ven? Integrací klíčových firemních systémů, nástrojů a aplikací.]]"
				/>
			</p>
			<p>
				<Formatted
					text="Zázraky na počkání sice neumíme, ale nebojíme se složité práce, a tak vám pomůžeme propojit (integrovat) téměř jakékoliv systémy a dosáhnout vyšší efektivity, a tím pádem i růstu podnikání. sp[[Když jiní tvrdí, že „to nejde“, my se pustíme do práce a děláme maximum, abychom dosáhli vytyčeného cíle.]]"
				/>
			</p>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
		>
			<Formatted text="Nezávazně poptat" />
			<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
		</Link>
	</Section>

	<Section
		background={Background.LightGradient}
		noPadding="bot"
		gridHeadline="container-8 lg:text-center"
		gridDesc="container-8"
		centered
	>
		{#snippet headline()}
			<Formatted text="Které cg[[problémy]] za vás díky správní integraci cg[[vyřešíme]]?" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-2 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getReasonsIntegrations(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} />
			{/each}
		</div>
	</Section>

	<Section centered gridHeadline="container-6" paddingFollowNext>
		{#snippet headline()}
			<Formatted text="Zařídíme integraci (téměř) cg[[jakýchkoliv systémů]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Pokud je to v našich silách, postaráme se v jakémkoliv [{getIndustriesPath(
						tenant,
					)}][[odvětví]] o integraci systémů, aplikací a nástrojů, které ve firmě používáte."
				/>
			</p>
		{/snippet}
	</Section>
	<ServicesCards cards={getSystems(tenant)} />

	<Section paddingFollowNext centered gridHeadline="container-8" gridDesc="container-6" gridChildren="container-6">
		{#snippet headline()}
			<Formatted text="Umíme i komplexní integrace. S čím už jsme si poradili?" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Kromě propojení základních firemních systémů a nástrojů máme sp[[zkušenosti také se specifickými a náročnějšími integracemi]]."
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		noPadding="top"
		gridHeadline="container-start-1 container-end-12 lg:container-end-7"
		gridDesc="container-start-1 container-end-12 lg:container-end-6"
		gridChildren="container-start-1 lg:container-start-8 container-end-12"
	>
		{#snippet desc()}
			<h2 class="mb-2"><Formatted text="Platební systémy a fakturační služby" /></h2>
			<p>
				<Formatted
					text="Napojení jednoduché platební brány nebo nebo fakturační služby na e-shop je hračka. Ale co když potřebujete integrovat platební systém s pokročilými funkcemi? Ve Fastest si s tím poradíme."
				/>
			</p>
			<p>
				<Formatted
					text="Typickým příkladem je sp[[pravidelná platba za předplatné]]. Chcete na svém webu či v aplikaci nabídnout placený obsah? Dokážeme pro vás nastavit automatické strhávání plateb nebo dodáme administrační rozhraní, kde budete mít všechna předplatná pod kontrolou. Přidat můžeme i klientskou zónu s podrobnostmi o platbách a tarifech."
				/>
			</p>
			<p>
				<Formatted
					text="Často řešíme i sp[[integraci v rámci objednávkového procesu]] (např. automatické přiřazení faktury po naskenování zásilky, provedení účetní kontroly nebo automatické odeslání e-mailu)."
				/>
			</p>
			<Link
				variant={ButtonVariant.Phlox}
				class="flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci kvalitní integraci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
		<h3 class="mt-8 lg:mt-72 mb-2"><Formatted text="Se kterými systémy máme zkušenosti?" /></h3>
		<ul class="custom-list">
			<li><Formatted text="sp[[Platební brány:]] GoPay, Comgate, Stripe, PayPal, ThePay" /></li>
			<li><Formatted text="sp[[Platební terminály:]] ČSOB Gapa, Sonet" /></li>
			<li><Formatted text="sp[[Fakturační systémy:]] iDoklad, Fakturoid, Pohoda, Helios, Money S3" /></li>
		</ul>
	</Section>

	<Section
		gridHeadline="container-start-1 container-end-12 lg:container-end-7"
		gridDesc="container-start-1 container-end-12 lg:container-end-6"
		gridChildren="container-start-1 lg:container-start-8 container-end-12"
	>
		{#snippet desc()}
			<h2 class="mb-2"><Formatted text="Skladová řešení s využitím IoT" /></h2>
			<p>
				<Formatted
					text="Klientům pomáháme také s integrací moderních skladových řešení: od základních evidenčních řešení až po ty, která fungují na principu IoT (Internet of Things – internet věcí). Při využití IoT spolu jednotlivá zařízení na skladě komunikují přes internet."
				/>
			</p>
			<p>
				<Formatted
					text="Naši klienti mají v oblibě především moderní RFID technologii, umožňující automatizované a přesné sledování zboží na skladu. Integrujeme jim RFID moduly a smart RFID čtečky, software pro ovládání čteček i další zařízení a nástroje."
				/>
			</p>
			<Link
				variant={ButtonVariant.Phlox}
				class="flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci zlepšit naše sklady" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
		<h3 class="mt-8 lg:mt-40 mb-2">
			<Formatted text="Čeho díky RFID technologii na skladu do sáhneme?" />
		</h3>
		<div class="flex flex-col md:flex-row md:gap-16">
			<ul class="custom-list">
				<li><Formatted text="Přesná a rychlá inventura" /></li>
				<li><Formatted text="Sledování pohybu zboží v reálném čase" /></li>
				<li><Formatted text="Okamžitá aktualizace databáze" /></li>
				<li><Formatted text="Příjem a výdej zboží bez manuálního skenování" /></li>
			</ul>
			<ul class="custom-list">
				<li><Formatted text="Přesná evidence ztrát" /></li>
				<li><Formatted text="Upozornění na neautorizovaný pohyb zboží" /></li>
				<li><Formatted text="Optimalizace skladových procesů" /></li>
				<li><Formatted text="Propojení s ERP a dalšími systémy" /></li>
			</ul>
		</div>
	</Section>

	<Section
		gridHeadline="container-start-1 container-end-12 lg:container-end-7"
		gridDesc="container-start-1 container-end-12 lg:container-end-5"
		gridChildren="container-start-1 lg:container-start-7 container-end-12"
	>
		{#snippet desc()}
			<h2 class="mb-2"><Formatted text="Bezpečné přihlášení a autentizace" /></h2>
			<p>
				<Formatted
					text="Bez zabezpečeného přihlášení a autentizace uživatelů dnes nejde provozovat prakticky žádnou aplikaci. Ale proč vymýšlet kolo, když už ho jednou vymysleli?"
				/>
			</p>
			<p>
				<Formatted
					text="Ve Fastest spoléháme na sp[[prověřenou a maximálně bezpečnou službu AWS Cognito]]. Snadno a rychle ji integrujeme do jakéhokoliv webu či aplikace, navíc je levnější, než kdyby si klient nechal řešení pro přihlašování vytvářet od nuly."
				/>
			</p>
			<Link
				variant={ButtonVariant.Phlox}
				class="flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Chci bezpečnou aplikaci" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}

		<h3 class="mt-8 lg:mt-52 mb-2">
			<Formatted text="Proč k přihlašování a autentizaci využíváme AWS Cognito?" />
		</h3>
		<div class="flex flex-col md:flex-row md:gap-16">
			<ul class="custom-list">
				<li><Formatted text="Všechny důležité funkce bez nutnosti vývoje od nuly" /></li>
				<li><Formatted text="Vysoká úroveň zabezpečení" /></li>
				<li><Formatted text="Snadné napojení na web či aplikaci" /></li>
			</ul>
			<ul class="custom-list">
				<li><Formatted text="Možnost dvoufázového ověření" /></li>
				<li><Formatted text="Úspora času i nákladů na vývoj" /></li>
				<li><Formatted text="Využití až pro miliony uživatelů" /></li>
			</ul>
		</div>
	</Section>

	<Section background={Background.LightGradient} centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="S cg[[integrací]] pomáháme zákazníkům cg[[napříč obory]]" />
		{/snippet}
	</Section>
	<LogoReferences logos={getPartnerLogos(partners, PartnerPlacement.Crm)} />

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Jak propojování systémů cg[[probíhá v praxi]]?" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getHowWeDoIntegrations(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			><Formatted text="Nezávazně poptat" />
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté otázky k cg[[integraci systémů]]" />
		{/snippet}
		<FaqItems faqs={getFaqsIntegrations(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-5 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[6 důvodů]], proč se s námi pustit do spolupráce" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
